import * as React from 'react';
import { cn } from '@utils';

interface IPictureProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	path: string;
	alt: string;
	className?: string;
}

/**
 * Image with blur-to-sharp transition on load.
 * Uses native `loading="lazy"` so offscreen images are not fetched eagerly; pass `loading="eager"` for above-the-fold images.
 */
function Picture({ className, path, alt, loading = 'lazy', ...restProps }: IPictureProps) {
	const [isLoaded, setIsLoaded] = React.useState(false);
	const imgRef = React.useRef<HTMLImageElement>(null);

	const markLoadedIfComplete = React.useCallback(() => {
		if (imgRef.current?.complete) {
			setIsLoaded(true);
		}
	}, []);

	React.useEffect(() => {
		setIsLoaded(false);
		markLoadedIfComplete();
	}, [path, markLoadedIfComplete]);

	return (
		<picture>
			<img
				ref={imgRef}
				className={cn('relative object-cover transition-[filter] duration-300', isLoaded ? 'blur-0' : 'blur-[5px]', className)}
				src={path}
				alt={alt}
				loading={loading}
				{...restProps}
				onLoad={event => {
					if (event.currentTarget.complete) {
						setIsLoaded(true);
					}
					restProps.onLoad?.(event);
				}}
			/>
		</picture>
	);
}

export default Picture;
